import { esc } from '../html';
import { LORE_DOCS } from './catalog';
import type { LoreRepository } from './repository';
import { archiveNarrative } from './story';
import type {
  LoreBlock,
  LoreDoc,
  LoreReliability,
  LoreSection,
  LoreTemplate,
} from './types';
import { LORE_SECTIONS } from './types';

const TEMPLATE_LABEL: Record<LoreTemplate, string> = {
  protocol: 'PROTOCOL / 协议文本',
  memo: 'MEMORANDUM / 备忘录',
  intake: 'INTAKE FORM / 登记表',
  transcript: 'TRANSCRIPT / 录音整理',
  ephemera: 'EPHEMERA / 印刷品',
  fragment: 'RECOVERED FRAGMENT / 残页',
  minutes: 'MINUTES / 会议纪要',
};

const RELIABILITY: Record<LoreReliability, { label: string; tone: string }> = {
  verified: { label: '已验证 / VERIFIED', tone: 'good' },
  partial: { label: '部分可信 / PARTIAL', tone: 'warn' },
  contradicted: { label: '存在矛盾 / CONTRADICTED', tone: 'bad' },
  redacted: { label: '大量涂改 / REDACTED', tone: 'bad' },
  unverified: { label: '来源不明 / UNVERIFIED', tone: 'warn' },
};

const SECTION_INDEX = new Map(LORE_SECTIONS.map((section, index) => [section.id, index]));

function reliabilityBadge(reliability: LoreReliability): string {
  const entry = RELIABILITY[reliability];
  return `<span class="doc-reliability" data-tone="${entry.tone}">${entry.label}</span>`;
}

function pips(owned: number, total: number): string {
  return `<span class="doc-pips" aria-label="碎片 ${owned}/${total}">${Array.from(
    { length: total },
    (_, index) => `<i class="${index < owned ? 'is-owned' : ''}"></i>`,
  ).join('')}</span>`;
}

function sectionOf(id: LoreSection) {
  return LORE_SECTIONS.find((section) => section.id === id) ?? LORE_SECTIONS[0];
}

function sectionNav(repo: LoreRepository, active: LoreSection): string {
  return LORE_SECTIONS.map((section) => {
    const progress = repo.getSectionProgress(section.id);
    const ratio = progress.fragmentsTotal
      ? Math.round((progress.fragmentsOwned / progress.fragmentsTotal) * 100)
      : 0;
    return `
      <button
        class="archive-nav-item ${section.id === active ? 'is-active' : ''}"
        type="button"
        data-action="archive-section"
        data-section="${section.id}"
      >
        <span class="archive-nav-code">${section.code}</span>
        <span class="archive-nav-copy">
          <b>${section.nameZh}</b>
          <small>${section.nameEn}</small>
        </span>
        <span class="archive-nav-count">${progress.fragmentsOwned}/${progress.fragmentsTotal}</span>
        <span class="archive-nav-meter"><i style="width:${ratio}%"></i></span>
      </button>`;
  }).join('');
}

function docCard(repo: LoreRepository, doc: LoreDoc, index: number): string {
  const progress = repo.getDocProgress(doc);
  if (progress.owned === 0) {
    return `
      <div class="doc-slot is-unknown" aria-label="未回收档案 ${index + 1}">
        <span class="doc-slot-index">SLOT ${String(index + 1).padStart(2, '0')}</span>
        <span class="doc-unknown-mark">?</span>
        <span class="doc-unknown-label">未回收 / UNRECOVERED</span>
      </div>`;
  }
  const stateLabel =
    progress.state === 'complete' ? '完整 / COMPLETE' : '残缺 / FRAGMENTARY';
  return `
    <button
      class="doc-slot is-recovered ${progress.state === 'complete' ? 'is-complete' : ''}"
      type="button"
      data-action="archive-doc"
      data-doc="${doc.id}"
    >
      <span class="doc-slot-index">${esc(doc.code)}</span>
      <span class="doc-slot-state">${stateLabel}</span>
      <span class="doc-slot-title">${esc(doc.title)}</span>
      <span class="doc-slot-sub">${TEMPLATE_LABEL[doc.template]}</span>
      <span class="doc-slot-foot">
        ${pips(progress.owned, progress.total)}
        <small>${progress.owned}/${progress.total} 碎片</small>
      </span>
    </button>`;
}

function renderBlock(block: LoreBlock): string {
  switch (block.type) {
    case 'p':
      return `<p>${esc(block.text)}</p>`;
    case 'h':
      return `<h3 class="doc-h">${esc(block.text)}</h3>`;
    case 'redacted':
      return `<div class="doc-redacted"><span>${esc(block.label ?? 'REDACTED')}</span>${
        block.text ? `<p>${esc(block.text)}</p>` : ''
      }<i></i><i></i></div>`;
    case 'quote':
      return `<blockquote>${esc(block.text)}${
        block.source ? `<cite>${esc(block.source)}</cite>` : ''
      }</blockquote>`;
    case 'list': {
      const tag = block.ordered ? 'ol' : 'ul';
      return `<${tag} class="doc-list">${block.items
        .map((item) => `<li>${esc(item)}</li>`)
        .join('')}</${tag}>`;
    }
    case 'table':
      return `<div class="doc-table-wrap"><table class="doc-table"><thead><tr>${block.head
        .map((cell) => `<th>${esc(cell)}</th>`)
        .join('')}</tr></thead><tbody>${block.rows
        .map((row) => `<tr>${row.map((cell) => `<td>${esc(cell)}</td>`).join('')}</tr>`)
        .join('')}</tbody></table></div>`;
    case 'stamp':
      return `<span class="doc-inline-stamp" data-tone="${block.tone ?? 'red'}">${esc(
        block.text,
      )}</span>`;
    case 'note':
      return `<aside class="doc-note">${esc(block.text)}</aside>`;
    case 'image':
      return `
        <button class="doc-image" type="button" data-action="archive-zoom" data-src="${esc(
          block.src,
        )}" data-alt="${esc(block.alt)}">
          <img src="${esc(block.src)}" alt="${esc(block.alt)}" loading="lazy" />
          ${block.caption ? `<span>${esc(block.caption)}</span>` : ''}
          <em>查看原图 / ENLARGE</em>
        </button>`;
    case 'audio':
      return `
        <div class="doc-audio">
          <div class="doc-audio-head">
            <span>RECOVERED AUDIO / 回收录音</span>
            <small>${esc(block.label)}</small>
          </div>
          <audio controls preload="none" src="${esc(block.src)}"></audio>
        </div>`;
    case 'divider':
      return '<hr class="doc-divider" />';
    default:
      return '';
  }
}

export function renderArchiveModal(
  repo: LoreRepository,
  sectionId: LoreSection,
  docId: string | null,
): string {
  const section = sectionOf(sectionId);
  const overall = repo.getOverallProgress();
  if (docId) {
    const doc = LORE_DOCS.find((entry) => entry.id === docId);
    if (doc && repo.getDocProgress(doc).owned > 0) {
      return renderArchiveDoc(repo, section, doc, overall);
    }
  }
  const progress = repo.getSectionProgress(section.id);
  const docs = LORE_DOCS.filter((doc) => doc.section === section.id);
  const narrative = archiveNarrative(overall.docsRecovered);
  const ratio = progress.fragmentsTotal
    ? Math.round((progress.fragmentsOwned / progress.fragmentsTotal) * 100)
    : 0;
  return `
    <div class="archive-shell">
      <header class="archive-head">
        <div class="archive-head-title">
          <span class="micro-label">SUBJECT ARCHIVE / 受试者档案室</span>
          <h2>碎片档案</h2>
          <p>被丢弃的规则、被涂改的记录，以及系统不希望受试者拼起来的那部分。</p>
        </div>
        <div class="archive-head-progress">
          <div><span>文档 / DOCUMENTS</span><b>${overall.docsRecovered} / ${overall.docsTotal}</b></div>
          <div><span>碎片 / FRAGMENTS</span><b>${overall.fragmentsOwned} / ${overall.fragmentsTotal}</b></div>
          <button class="icon-button" type="button" data-action="close-modal" aria-label="关闭档案室">CLOSE</button>
        </div>
      </header>
      <div class="archive-narrative">
        <span>${esc(narrative.kicker)}</span>
        <p>${esc(narrative.text)}</p>
      </div>
      <div class="archive-layout">
        <nav class="archive-nav" aria-label="档案分类">${sectionNav(repo, section.id)}</nav>
        <section class="archive-content">
          <header class="archive-section-head">
            <div>
              <span class="archive-section-code">${section.code} / ${section.nameEn}</span>
              <h3>${section.nameZh}</h3>
              <p>${section.blurb}</p>
            </div>
            <div class="archive-section-stats">
              <span><b>${progress.missingDocs}</b> 篇未回收</span>
              <span><b>${progress.fragmentsOwned}</b> / ${progress.fragmentsTotal} 碎片</span>
              <span>${progress.docsComplete} 篇完整</span>
            </div>
          </header>
          <div class="archive-progress-track"><i style="width:${ratio}%"></i><b>${ratio}%</b></div>
          <div class="archive-slots">
            ${docs.map((doc, index) => docCard(repo, doc, index)).join('')}
          </div>
          <footer class="archive-foot">
            <span>未回收条目只保留位置编号。RANDOM RECOVERY / 难度越高，越可能回收深层记录。</span>
            <span>SECTION ${String((SECTION_INDEX.get(section.id) ?? 0) + 1).padStart(2, '0')} / 07</span>
          </footer>
        </section>
      </div>
    </div>`;
}

function renderArchiveDoc(
  repo: LoreRepository,
  section: (typeof LORE_SECTIONS)[number],
  doc: LoreDoc,
  overall: ReturnType<LoreRepository['getOverallProgress']>,
): string {
  const progress = repo.getDocProgress(doc);
  const fragments = doc.fragments
    .map((fragment, index) => {
      const owned = repo.isOwned(fragment.id);
      if (!owned) {
        return `
          <section class="doc-fragment is-missing">
            <header>
              <span>FRAGMENT ${String(index + 1).padStart(2, '0')}</span>
              <b>未回收 / UNRECOVERED</b>
            </header>
            <div class="doc-missing-body">
              <span>?</span>
              <p>该碎片仍散落在校准流程中。继续受试，或提高协议等级。</p>
            </div>
          </section>`;
      }
      return `
        <section class="doc-fragment">
          <header>
            <span>FRAGMENT ${String(index + 1).padStart(2, '0')}</span>
            <b>${esc(fragment.label)}</b>
          </header>
          <div class="doc-body">${fragment.blocks.map(renderBlock).join('')}</div>
        </section>`;
    })
    .join('');

  return `
    <div class="archive-shell is-doc">
      <header class="archive-doc-top">
        <button class="link-button archive-back" type="button" data-action="archive-back">← 返回 ${section.nameZh}</button>
        <div class="archive-doc-progress">
          <span>碎片 ${progress.owned}/${progress.total}</span>
          <span>总档案 ${overall.fragmentsOwned}/${overall.fragmentsTotal}</span>
          <button class="icon-button" type="button" data-action="close-modal" aria-label="关闭档案室">CLOSE</button>
        </div>
      </header>
      <div class="archive-doc-scroll">
        <article class="doc-sheet" data-template="${doc.template}">
          <div class="doc-classification">
            <span>${esc(doc.code)} / ${TEMPLATE_LABEL[doc.template]}</span>
            <span>${esc(doc.date)}</span>
          </div>
          <header class="doc-head">
            <span class="doc-section-tag">${section.code} · ${section.nameZh}</span>
            <h2>${esc(doc.title)}</h2>
            ${doc.subtitle ? `<p class="doc-subtitle">${esc(doc.subtitle)}</p>` : ''}
            <div class="doc-meta">
              ${reliabilityBadge(doc.reliability)}
              <span>ACCESS / 协议 ${doc.minProtocol}+</span>
              <span>${progress.state === 'complete' ? '完整卷宗' : '部分回收'}</span>
            </div>
          </header>
          <div class="doc-fragments">${fragments}</div>
          <footer class="doc-signoff">
            <div class="doc-stamp-row">
              <span class="doc-inline-stamp" data-tone="ink">VESPER BEHAVIOURAL RESEARCH</span>
              <span class="doc-inline-stamp" data-tone="muted">SITE 06 / ARCHIVE COPY</span>
            </div>
            <div class="doc-tags">${doc.tags.map((tag) => `<span>#${esc(tag)}</span>`).join('')}</div>
          </footer>
        </article>
      </div>
    </div>`;
}
